/**
 * Amortissements étendus aux CAPEX non-véhicules.
 * (28/05/2026 v2 — Phase B requested by user)
 *
 * Catégories et durées (linéaire, normes comptables FR) :
 *   • Dépôts (bâtiments + aménagements lourds) ........ 20 ans / 240 mois
 *   • Bureau / Siège social (équipement administratif)   9 ans / 108 mois
 *   • Logiciels (TMS, ERP, certif qualité numérique) ... 3 ans /  36 mois
 *   • Site internet (marketing digital) ................ 2 ans /  24 mois
 *
 * Source des données :
 *   - investments.js  → catalogue technique des INVESTMENTS (id, costBuy)
 *   - officeUpgrades.js → catalogue des équipements de bureau
 *   - financeSlice.investmentPurchaseDays → date d'achat ({id} → dayNum)
 *   - officeSlice.officeUpgrades → date d'achat ({id} → { boughtAt })
 *
 * Sortie de `computeCapexDepreciation(state)` :
 *   {
 *     items: [{ id, label, category, price, vnc, monthlyDotation, dailyDotation, remainingMonths, totalMonths }],
 *     totalDailyDotation, totalMonthlyDotation, totalAnnualDotation,
 *     totalGrossValue, totalVnc,
 *     byCategory: { depot: {...}, office: {...}, software: {...}, website: {...} },
 *   }
 *
 * IMPORTANT : ce module N'INCLUT PAS la flotte (déjà gérée par
 * `computeFleetDepreciation` dans `depreciation.js`) — il vient EN PLUS,
 * pour les autres CAPEX.
 */
import { INVESTMENTS } from '../data/investments';
import { OFFICE_UPGRADES } from '../data/officeUpgrades';

const DAYS_PER_MONTH_ACCOUNTING = 30;
const DAYS_PER_YEAR_ACCOUNTING = 360;

export const CAPEX_DURATIONS = {
  depot: 240,    // 20 ans
  office: 108,   // 9 ans
  software: 36,  // 3 ans
  website: 24,   // 2 ans
};

export const CAPEX_CATEGORY_LABELS = {
  depot: 'Dépôts & bâtiments',
  office: 'Bureau & siège social',
  software: 'Logiciels',
  website: 'Site internet',
};

/**
 * Mapping investment-id → capex category.
 *
 * Investments WITHOUT a real `costBuy` (location pure, contrat fournisseur,
 * tickets resto…) are EXCLUDED — they génèrent une charge mensuelle directe,
 * pas une immobilisation amortissable.
 */
export const INVESTMENT_CAPEX_CATEGORY = {
  // Dépôts
  'inv-depot-01': 'depot',
  'inv-depot-03': 'depot',
  'inv-depot-04': 'depot',
  'inv-depot-05': 'depot',
  // 'inv-depot-02' = location pure (costBuy 0) → excluded
  // 'inv-energy-01' = cuve carburant 4 500 € → depot category (équipement de site)
  'inv-energy-01': 'depot',
  'inv-energy-02': 'depot',
  // 'inv-energy-03' = contrat fournisseur (costBuy 0) → excluded
  // Bureau & Siège
  'inv-office-01': 'office',
  // Logiciels
  'inv-tech-01': 'software', // Logiciel TMS avancé
  'inv-tech-04': 'software', // Certification qualité (logiciel + démarche)
  // Site internet & marketing digital
  'inv-tech-02': 'website',  // Site web + référencement
  'inv-tech-03': 'website',  // Campagne marketing régionale
};

/**
 * Mapping office-upgrade-id → capex category (always 'office').
 * Office upgrades with costBuy === 0 (canteen, gym = service) are excluded.
 */
export const OFFICE_UPGRADE_CAPEX_CATEGORY = {
  'office-cleaning': 'office',
  'office-fountain': 'office',
  'office-coffee': 'office',
  'office-restroom': 'office',
  'office-plants': 'office',
  // 'office-canteen' (0 €), 'office-gym' (0 €) → excluded
};

const _INV_BY_ID = Object.fromEntries(INVESTMENTS.map(i => [i.id, i]));
const _OFFICE_BY_ID = Object.fromEntries(OFFICE_UPGRADES.map(u => [u.id, u]));

/**
 * Compute depreciation for a single capex item.
 * Returns null if the item has no `price` (>0) or no category.
 */
export function computeCapexItem({ id, label, price, category, purchaseDay }, totalDays) {
  if (!price || price <= 0) return null;
  const months = CAPEX_DURATIONS[category];
  if (!months) return null;
  const ageDays = Math.max(0, (totalDays || 0) - (purchaseDay || 0));
  const ageMonths = ageDays / DAYS_PER_MONTH_ACCOUNTING;
  const ratio = Math.min(1, ageMonths / months);
  const cumulativeDepreciation = Math.round(price * ratio);
  const vnc = Math.max(0, price - cumulativeDepreciation);
  const monthlyDotation = ratio < 1 ? Math.round(price / months) : 0;
  const dailyDotation = ratio < 1 ? price / months / DAYS_PER_MONTH_ACCOUNTING : 0;
  const remainingMonths = Math.max(0, Math.ceil(months - ageMonths));
  return {
    id,
    label,
    category,
    price,
    vnc,
    cumulativeDepreciation,
    monthlyDotation,
    dailyDotation,
    remainingMonths,
    totalMonths: months,
  };
}

/**
 * Aggregate depreciation across all NON-FLEET capex held by the player.
 * Reads from `state.activeInvestments`, `state.investmentPurchaseDays`,
 * and `state.officeUpgrades`.
 */
export function computeCapexDepreciation(state) {
  const totalDays = state?.totalDays || 0;
  const activeInv = state?.activeInvestments || {};
  const purchaseDays = state?.investmentPurchaseDays || {};
  const officeUpg = state?.officeUpgrades || {};

  const items = [];

  // 1. Regular investments
  for (const invId of Object.keys(activeInv)) {
    if (!activeInv[invId]) continue;
    const cat = INVESTMENT_CAPEX_CATEGORY[invId];
    if (!cat) continue;
    const inv = _INV_BY_ID[invId];
    if (!inv || !inv.costBuy || inv.costBuy <= 0) continue;
    const item = computeCapexItem({
      id: invId,
      label: inv.name,
      price: inv.costBuy,
      category: cat,
      purchaseDay: purchaseDays[invId] != null ? purchaseDays[invId] : 0,
    }, totalDays);
    if (item) items.push(item);
  }

  // 2. Office upgrades
  for (const upgId of Object.keys(officeUpg)) {
    const entry = officeUpg[upgId];
    if (!entry) continue;
    const cat = OFFICE_UPGRADE_CAPEX_CATEGORY[upgId];
    if (!cat) continue;
    const upg = _OFFICE_BY_ID[upgId];
    if (!upg || !upg.costBuy || upg.costBuy <= 0) continue;
    const item = computeCapexItem({
      id: upgId,
      label: upg.name,
      price: upg.costBuy,
      category: cat,
      purchaseDay: entry.boughtAt != null ? entry.boughtAt : 0,
    }, totalDays);
    if (item) items.push(item);
  }

  // Aggregates
  let totalDailyDotation = 0;
  let totalGrossValue = 0;
  let totalVnc = 0;
  const byCategory = {};
  for (const cat of Object.keys(CAPEX_DURATIONS)) {
    byCategory[cat] = { count: 0, grossValue: 0, vnc: 0, monthlyDotation: 0, items: [] };
  }
  for (const it of items) {
    totalDailyDotation += it.dailyDotation;
    totalGrossValue += it.price;
    totalVnc += it.vnc;
    const slot = byCategory[it.category];
    slot.count += 1;
    slot.grossValue += it.price;
    slot.vnc += it.vnc;
    slot.monthlyDotation += it.monthlyDotation;
    slot.items.push(it);
  }
  return {
    items,
    totalDailyDotation,
    // 28/05/2026 v2 — keep the header aggregate strictly equal to the sum
    // of the per-category cards so a learner who adds the cards lands
    // exactly on the header.
    totalMonthlyDotation: Object.values(byCategory).reduce((s, slot) => s + slot.monthlyDotation, 0),
    totalAnnualDotation: Math.round(totalDailyDotation * DAYS_PER_YEAR_ACCOUNTING),
    totalGrossValue,
    totalVnc,
    byCategory,
  };
}
